import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BrandLogo from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signInWithPassword, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await signInWithPassword(email, password);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Could not sign in');
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(error.message || 'Could not start Google sign-in');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <BrandLogo className="h-14 w-14" />
            <div>
              <h1 className="text-3xl font-serif font-semibold text-[#2D2A26]">Katha Mithra</h1>
              <p className="text-sm text-[#6B665E]">कथा मित्र</p>
            </div>
          </div>
          <h2 className="text-2xl font-serif text-[#2D2A26]">Welcome Back</h2>
          <p className="text-[#6B665E] mt-2">Sign in to continue</p>
        </div>

        <div className="bg-white rounded-xl border border-[#E6E2D6] p-5 sm:p-8 shadow-card">
          <Button
            type="button"
            disabled={loading}
            onClick={handleGoogle}
            className="w-full rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-card"
            data-testid="login-google-btn"
          >
            Continue with Google
          </Button>

          <div className="my-6 flex items-center space-x-3">
            <div className="flex-1 h-px bg-[#E6E2D6]" />
            <span className="text-xs uppercase text-[#6B665E]">or</span>
            <div className="flex-1 h-px bg-[#E6E2D6]" />
          </div>

          <form onSubmit={handlePassword} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="email-input"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="password-input"
                className="mt-2"
              />
            </div>

            <Button
              type="submit"
              variant="outline"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 text-lg font-medium"
              data-testid="login-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#6B665E]">
              New here?{' '}
              <Link to="/register" className="text-[#C05621] hover:underline font-medium">
                Create an account
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#6B665E] hover:text-[#C05621]">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
